import { format } from "date-fns";
import { useMemo } from "react";
import { generatePath, useNavigate, useOutletContext, useParams } from "react-router";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { GaiaMapUIOverlay } from "~/modules/map/components/GaiaMapUIOverlay";
import { MapButtonFitBounds } from "~/modules/map/components/MapButtonFitBounds";
import { useGaiaMapBounds } from "~/modules/map/hooks/useGaiaMapBounds";
import { NETHERLANDS_BOUNDS } from "~/modules/map/mapConstants";
import { AreasLayerV2 } from "~/modules/plannerV2/components/map-layers/AreasLayerV2";
import { PLANNER_MAP_PADDING, PLANNERV2_DEFAULT_BOUNDS } from "~/modules/plannerV2/constants/map";

import { PlannerV2ContextType } from "../types";
import { PlannerV2AreaFilters } from "./components/PlannerV2AreaFilters";
import { PlannerV2AreaLists } from "./components/PlannerV2AreaLists";
import { usePlannerV2AreaFilters } from "./hooks/usePlannerV2AreaFilters";
import useGetAllPlannerV2AreasGetInBoundingBox from "./usePlannerV2AreasGetInBoundingBox";

export function PlannerV2AreasScreen() {
    const { date } = useParams();

    if (!date) {
        throw new Error("Parameter `date` is not provided");
    }

    const navigate = useNavigate();
    const { boundsWithoutPadding } = useGaiaMapBounds();
    const { filterValues } = usePlannerV2AreaFilters();

    const params = useMemo(
        () => ({
            ...filterValues,
            onDate: format(parseStringToDate(date), "yyyy-MM-dd"),
            boundingBox: boundsWithoutPadding?.toArray().flat() ?? PLANNERV2_DEFAULT_BOUNDS,
        }),
        [filterValues, boundsWithoutPadding, date],
    );

    const { data, loading } = useGetAllPlannerV2AreasGetInBoundingBox(params);
    const { plannerSharedLeftPanel, plannerSharedBottomRightPanel } = useOutletContext<PlannerV2ContextType>();

    const createPlannerVisitHref = (plannerAreaId: string, plannerVisitId: string) =>
        generatePath("areas/:plannerAreaId/visits/:plannerVisitId", { plannerAreaId, plannerVisitId });

    const createPlannerAreaHref = (plannerAreaId: string) => generatePath("areas/:plannerAreaId", { plannerAreaId });

    const handleOnPlannerAreaClick = (plannerAreaId: string) => {
        navigate(createPlannerAreaHref(plannerAreaId));
    };

    return (
        <>
            <GaiaMapUIOverlay
                leftPanel={
                    <>
                        {plannerSharedLeftPanel}
                        <PlannerV2AreaFilters maxRequiredFieldworkers={data.maxRequiredFieldworkers} />
                    </>
                }
                rightPanel={
                    <PlannerV2AreaLists
                        loading={loading}
                        plannerAreasPerStatusBucket={data.plannerAreasPerStatusBucket}
                        visitOpenHref={createPlannerVisitHref}
                        areaNameHref={createPlannerAreaHref}
                    />
                }
                bottomRightPanel={plannerSharedBottomRightPanel}
                bottomBar={<MapButtonFitBounds bounds={NETHERLANDS_BOUNDS} padding={PLANNER_MAP_PADDING} />}
            />

            {data.plannerAreas && (
                <AreasLayerV2 areas={data.plannerAreas} areasById={data.plannerAreaById} onAreaClick={handleOnPlannerAreaClick} />
            )}
        </>
    );
}
